import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Input } from 'antd';
import { ApplicationState } from '../../../../reducers';
import { AccountConfigurationList } from '../../../../../types/account_configurations';
import { typingDone } from '../../../../utils/Utils';
import { AsyncDispatch } from '../../../../../types/global';
import { useAppDispatch } from '../../../../store/ReduxStore';
import { fetchAccountDynamicFieldLists } from '../../../../actions/parent/accountConfigurations/fetch';
import AntMultiSelect from '../../../global/AntMultiSelect';
import AccountDynamicFieldListsTable from './AccountDynamicFieldListsTable';

const AccountDynamicFieldListsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const [searchKeywords, setSearchKeywords] = React.useState<string>('');
  const [selectedListIds, setSelectedListIds] = React.useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>([]);
  const [filteredData, setFilteredData] = React.useState<AccountConfigurationList[]>([]);
  const [selectedCustomFieldIds, setSelectedCustomFieldIds] = React.useState<number[] | undefined>(
    undefined
  );
  const [sortColumn, setSortColumn] = React.useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = React.useState<string | undefined>(undefined);

  const { lists, isLoading, customFields } = useSelector((state: ApplicationState) => {
    return {
      lists: state.parent.configurations.dynamicFieldLists.data,
      isLoading: state.parent.configurations.dynamicFieldLists.isLoading,
      customFields: state.parent.parentCustomFields.customFields,
    };
  }, shallowEqual);

  const nameListValues = React.useMemo(() => lists.map(l => ({ id: l.id, name: l.name })), [lists]);
  const userListValues = React.useMemo(() => {
    const uniqueUsers = new Map<string, { id: number; name: string }>();

    lists.forEach(l => {
      if (l.user_id && l.created_by) {
        if (!uniqueUsers.has(l.created_by)) {
          uniqueUsers.set(l.created_by, {
            id: l.user_id,
            name: l.created_by,
          });
        }
      }
    });

    return Array.from(uniqueUsers.values());
  }, [lists]);

  const userNameToIdsMap = React.useMemo(() => {
    const usersMap = new Map<string, number[]>();

    lists.forEach(l => {
      if (l.user_id && l.created_by) {
        if (!usersMap.has(l.created_by)) {
          usersMap.set(l.created_by, []);
        }
        usersMap.get(l.created_by)!.push(l.user_id);
      }
    });

    return usersMap;
  }, [lists]);

  const handleCustomFieldsFilter = (value: number[]) => {
    const ids = value.length > 0 ? value : undefined;
    setSelectedCustomFieldIds(ids);
    dispatch(fetchAccountDynamicFieldLists(ids));
  };

  const handleSort = (column: string, order: string) => {
    setSortColumn(column);
    setSortOrder(order);
  };

  const filterListByColumn = React.useCallback(
    (data: AccountConfigurationList[]) => {
      let filtered = [...data];

      if (selectedListIds.length > 0) {
        filtered = filtered.filter(list => selectedListIds.includes(list.id));
      }

      if (selectedUserIds.length > 0) {
        const selectedUserNames = lists
          .filter(list => selectedUserIds.includes(list.user_id || 0))
          .map(list => list.created_by)
          .filter((name): name is string => name !== null);
        filtered = filtered.filter(
          list => list.created_by && selectedUserNames.includes(list.created_by)
        );
      }

      return filtered;
    },
    [selectedListIds, selectedUserIds, lists]
  );

  const sortList = React.useCallback(
    (data: AccountConfigurationList[]) => {
      if (!sortColumn || !sortOrder) {
        return data;
      }

      const sortedData = [...data];
      sortedData.sort((a, b) => {
        let aValue = '';
        let bValue = '';

        if (sortColumn === 'name') {
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
        } else if (sortColumn === 'created_by') {
          aValue = a.created_by?.toLowerCase() || '';
          bValue = b.created_by?.toLowerCase() || '';
        }

        if (aValue < bValue) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
      });

      return sortedData;
    },
    [sortColumn, sortOrder]
  );

  const handleFilterSort = React.useCallback(() => {
    let filteredLists = [...lists];

    if (searchKeywords) {
      const lowerKeywords = searchKeywords.toLowerCase();
      filteredLists = filteredLists.filter((list: AccountConfigurationList) =>
        list.name.toLowerCase().includes(lowerKeywords)
      );
    }

    filteredLists = filterListByColumn(filteredLists);
    const sortedData = sortList(filteredLists);
    setFilteredData(sortedData);
  }, [lists, searchKeywords, filterListByColumn, sortList]);

  React.useEffect(() => {
    if (searchKeywords) {
      typingDone(handleFilterSort);
    } else {
      handleFilterSort();
    }
  }, [handleFilterSort, searchKeywords]);

  return (
    <div className="page-layout">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <Input
            className="max-w-xl mr-2"
            value={searchKeywords}
            onChange={e => setSearchKeywords(e.target.value)}
            placeholder={t('configurations:searchDynamicLists')}
            allowClear
          />
          <AntMultiSelect
            className="max-w-md"
            selectedElements={selectedCustomFieldIds || []}
            onChange={handleCustomFieldsFilter}
            placeholder={t('configurations:filterByCustomFields')}
            allowClear
            style={{ minWidth: '300px' }}
            elements={customFields.map(field => ({
              id: field.id,
              name: field.name,
            }))}
            showSearch
          />
        </div>
      </div>
      <div className="page-layout__content flex w-full">
        {isLoading && filteredData.length === 0 ? (
          <div className="loader" />
        ) : (
          <AccountDynamicFieldListsTable
            filteredData={filteredData}
            isLoading={isLoading}
            selectedListIds={selectedListIds}
            setSelectedListIds={setSelectedListIds}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            nameListValues={nameListValues}
            userListValues={userListValues}
            userNameToIdsMap={userNameToIdsMap}
          />
        )}
      </div>
    </div>
  );
};

export default AccountDynamicFieldListsPage;
